import { Link, useLocation, useOutletContext } from "react-router";
import { Icon } from "@/components/icons";
import type { AppOutletContext } from "@/src/layouts/AppLayout";
import { NAV_ACCESS, isAccountTypeAllowed, type NavAccessKey } from "@/src/lib/nav-access";

type Item = {
  href: string;
  label: string;
  icon: string;
  navAccessKey?: NavAccessKey;
};

const ITEMS: Item[] = [
  { href: "/account-management", label: "Account", icon: "person" },
  { href: "/company-settings", label: "Company", icon: "groups" },
  { href: "/api-settings", label: "API & Webhooks", icon: "key", navAccessKey: "apiSettings" },
];

function isActive(pathname: string, href: string) {
  return pathname === href || pathname.startsWith(href + "/");
}

export function SettingsNav() {
  const pathname = useLocation().pathname;
  const ctx = useOutletContext<AppOutletContext | null>();
  const items = ITEMS.filter(
    (item) =>
      !item.navAccessKey ||
      isAccountTypeAllowed(ctx?.apiUser?.accountType, NAV_ACCESS[item.navAccessKey]),
  );

  return (
    <aside
      aria-label="Settings sections"
      className="sticky top-0 z-10 flex shrink-0 flex-col gap-2 border-b border-grey-200 bg-grey-25 p-3 md:h-screen md:w-[200px] md:gap-2.5 md:self-start md:overflow-y-auto md:border-b-0 md:border-r"
    >
      <p className="px-1.5 text-h4 text-black">Settings</p>
      <div className="hidden h-px w-full bg-grey-100 md:block" />
      <nav className="flex flex-row gap-1 overflow-x-auto md:flex-col md:overflow-x-visible [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {items.map((item) => {
          const active = isActive(pathname, item.href);
          return (
            <Link
              key={item.href}
              to={item.href}
              aria-current={active ? "page" : undefined}
              aria-label={item.label}
              title={item.label}
              className={[
                "flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-[4px] p-2 text-small font-medium transition-colors",
                active
                  ? "bg-grey-950 text-white border-b border-black shadow-flat-black"
                  : "text-black hover:bg-grey-100",
              ].join(" ")}
            >
              <Icon
                name={item.icon}
                size={20}
                filled={active}
                className={active ? "text-white" : "text-grey-900"}
              />
              <span className={active ? "" : "hidden md:inline"}>{item.label}</span>
            </Link>
          );
        })}
      </nav>
    </aside>
  );
}
